import {DatePipe, DecimalPipe} from '@angular/common';
import {Inject, LOCALE_ID, Pipe, PipeTransform} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {SafeHtml} from '@angular/platform-browser';
import {Locale} from './locale';

@Pipe({
  name: 'dateFormat',
  pure: false
})
export class DateFormatPipe extends DatePipe implements PipeTransform {

  constructor(private _translateService: TranslateService,
              private numberPipe: DecimalPipe,
              @Inject(LOCALE_ID) private localeId: string) {
    super(Locale.getLocale(_translateService, localeId));
  }

  static checkDate(obj: any, field: string) {
    if (typeof obj[field] === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(obj[field])) {
      const [day, month, year] = obj[field].split('/').map(Number);
      obj[field] = new Date(Date.UTC(year, month - 1, day));
    } else if (typeof obj[field] === 'string' && /^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}:\d{2}$/.test(obj[field])) {
      const [datePart, timePart] = obj[field].split(' ');
      const [day, month, year] = datePart.split('/').map(Number);
      const [hours, minutes, seconds] = timePart.split(':').map(Number);
      obj[field] = new Date(year, month - 1, day, hours, minutes, seconds);
    } else if (!(obj[field] instanceof Date) && new Date(obj[field]) && !isNaN(new Date(obj[field]).getTime())) {
      obj[field] = new Date(
        new Date(obj[field]).getFullYear(),
        new Date(obj[field]).getMonth(),
        new Date(obj[field]).getDate(),
        new Date(obj[field]).getHours(),
        new Date(obj[field]).getMinutes(),
        new Date(obj[field]).getSeconds(),
        new Date(obj[field]).getMilliseconds()
        );
    }
  }

  static isAMPattern(locale: string): boolean {
    return locale === Locale.LANGS.EN_US.id;
  }

  private static getDatePattern(locale: string) {
    return DateFormatPipe.getDateFirstPattern(locale) + '/' + DateFormatPipe.getDateSecondPattern(locale) + '/yyyy';
  }

  private static getTimeFirstPattern(locale: string) {
    let pattern;
    switch (locale) {
      case Locale.LANGS.EN_US.id:
        pattern = 'hh';
        break;
      case Locale.LANGS.FR_FR.id:
      case Locale.LANGS.EN_GB.id:
      default:
        pattern = 'HH';
        break;
    }

    return pattern;
  }

  private static getMinutesPattern(locale: string) {
    let pattern;
    switch (locale) {
      case Locale.LANGS.EN_US.id:
        pattern = 'mm';
        break;
      case Locale.LANGS.FR_FR.id:
      case Locale.LANGS.EN_GB.id:
      default:
        pattern = 'mm';
        break;
    }

    return pattern;
  }

  private static getDateFirstPattern(locale: string) {
    let pattern;
    switch (locale) {
      case Locale.LANGS.EN_US.id:
        pattern = 'M';
        break;
      case Locale.LANGS.FR_FR.id:
      case Locale.LANGS.EN_GB.id:
      default:
        pattern = 'dd';
        break;
    }

    return pattern;
  }

  private static getDateSecondPattern(locale: string) {
    let pattern;
    switch (locale) {
      case Locale.LANGS.EN_US.id:
        pattern = 'd';
        break;
      case Locale.LANGS.FR_FR.id:
      case Locale.LANGS.EN_GB.id:
      default:
        pattern = 'MM';
        break;
    }

    return pattern;
  }

  // @ts-ignore
  transform(value: any, args?: any): SafeHtml {
    let html = '';
    const locale = Locale.getLocale(this._translateService, this.localeId);
    switch (args) {
      case 'customDateFirst':
        html += super.transform(value, DateFormatPipe.getDateFirstPattern(locale), null, locale);
        break;
      case 'customDateSecond':
        html += super.transform(value, DateFormatPipe.getDateSecondPattern(locale), null, locale);
        break;
      case 'customDate':
        html += super.transform(value, DateFormatPipe.getDatePattern(locale), null, locale);
        break;
      case 'customDateTime':
        html += '' +
          '<span class="date-format-date">' +
          super.transform(value, DateFormatPipe.getDatePattern(locale), null, locale) +
          '</span>' +
          ' - ' +
          '<span class="date-format-time">' +
          super.transform(value, DateFormatPipe.getTimeFirstPattern(locale), null, locale) +
          '</span>' +
          ':' +
          '<span class="date-format-time">' +
          super.transform(value, 'mm', null, locale) +
          '</span>' +
          ':' +
          '<span class="date-format-time">' +
          super.transform(value, 'ss', null, locale) +
          '</span>';
        if (DateFormatPipe.isAMPattern(locale)) {
          html += ' <span class="">' + super.transform(value, 'a', null, locale) + '</span>';
        }
        break;
      case 'customDateTimeDot':
        html += '' +
          '<span class="date-format date-format-date">' +
          super.transform(value, DateFormatPipe.getDatePattern(locale), null, locale) +
          '</span>  ' +
          '<span class="dot"></span>' +
          '<span class="date-format date-format-time">' +
          super.transform(value, DateFormatPipe.getTimeFirstPattern(locale), null, locale) +
          '</span>' +
          ' : ' +
          '<span class="date-format date-format-time">' +
          super.transform(value, 'mm', null, locale) +
          '</span>' +
          ' : ' +
          '<span class="date-format date-format-time">' +
          super.transform(value, 'ss', null, locale) +
          '</span>';
        if (DateFormatPipe.isAMPattern(locale)) {
          html += ' <span class="date-format date-format-time-am-pm">' + super.transform(value, 'a', null, locale) + '</span>';
        }
        break;
      case 'customDateTimeExecutionCard':
        html += '' +
          '<span class="date-format date-format-date">' +
          super.transform(value, DateFormatPipe.getDatePattern(locale), null, locale) +
          '</span>  ' +

          '<span class="date-format date-format-time">' +
          super.transform(value, DateFormatPipe.getTimeFirstPattern(locale), null, locale) +
          '</span>' +
          ':' +
          '<span class="date-format date-format-time">' +
          super.transform(value, 'mm', null, locale) +
          '</span>' +
          ':' +
          '<span class="date-format date-format-time">' +
          super.transform(value, 'ss', null, locale) +
          '</span>';
        if (DateFormatPipe.isAMPattern(locale)) {
          html += ' <span class="date-format date-format-time-am-pm">' + super.transform(value, 'a', null, locale) + '</span>';
        }
        break;
      case 'customDateTimeAudit':
        html += '' +
          super.transform(value, DateFormatPipe.getDatePattern(locale), null, locale) +
          ' ' + super.transform(value, DateFormatPipe.getTimeFirstPattern(locale), null, locale) +
          ':' + super.transform(value, 'mm', null, locale) +
          ':' + super.transform(value, 'ss', null, locale);
        if (DateFormatPipe.isAMPattern(locale)) {
          html += super.transform(value, 'a', null, locale);
        }
        break;
      case 'countdown':
        const absValue = Math.abs(value);
        const min = 60;
        const hour = min * 60;
        const day = hour * 24;
        html += value > 0 ? '<div>−</div>' : '<div class="visibility-hidden">−</div>';
        html += absValue > day ?
          '<div>' + this.numberPipe.transform(Math.floor(absValue / day), '3.0-0') +
          '</div>' +
          ' <div>:</div> ' :
          '';
        html += '<div>' + this.numberPipe.transform(Math.floor(absValue / hour) % 24, '2.0-0') + '</div>' +
          ' <div>:</div> ' +
          '<div>' +
          this.numberPipe.transform(Math.floor(absValue / min) % 60, '2.0-0') +
          '</div>' +
          '<div>:</div> ' +
          '<div>' +
          this.numberPipe.transform(Math.floor(absValue % 60), '2.0-0') +
          '</div>';
        break;
      default:
        html += super.transform(value, args, null, locale);
    }

    return html;
  }
}
